import { useState, useEffect } from 'react';
import { gameService } from '../services/gameService';
import { GameSession, Player, Definition } from '../types';

export function useGame(gameId: string | null) {
  const [game, setGame] = useState<GameSession | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!gameId) {
      console.log('useGame: No game ID provided');
      setLoading(false);
      return;
    }

    const fetchGame = async () => {
      try {
        setLoading(true);
        setError(null);
        console.log('useGame: Fetching game with ID:', gameId);
        
        // First check if game exists
        console.log('useGame: Verifying game existence');
        const exists = await gameService.verifyGame(gameId);
        console.log('useGame: Game exists:', exists);
        
        if (!exists) {
          console.error('useGame: Game not found with ID:', gameId);
          setError('Game not found');
          setLoading(false);
          return;
        }

        // Set up real-time listener
        console.log('useGame: Setting up real-time listener');
        const unsubscribe = gameService.onGameUpdate(gameId, (updatedGame) => {
          console.log('useGame: Received game update:', {
            id: updatedGame?.id,
            exists: !!updatedGame,
            phase: updatedGame?.state?.phase,
            playerCount: updatedGame?.state?.players?.length
          });
          
          if (updatedGame) {
            setGame(updatedGame);
          } else {
            console.error('useGame: Game not found in real-time update');
            setError('Game not found');
          }
          setLoading(false);
        });

        return () => {
          console.log('useGame: Cleaning up real-time listener');
          unsubscribe();
        };
      } catch (err) {
        const errorMessage = err instanceof Error ? err.message : 'Failed to load game';
        console.error('useGame: Error fetching game:', errorMessage);
        setError(errorMessage);
        setLoading(false);
      }
    };

    fetchGame();
  }, [gameId]);

  const createGame = async (readerId: string, readerName: string) => {
    setError(null);
    try {
      console.log('useGame: Creating new game for reader:', readerId, 'name:', readerName);
      const newGameId = await gameService.createGame(readerId, readerName);
      console.log('useGame: Game created successfully with ID:', newGameId);
      return newGameId;
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      console.error('useGame: Failed to create game:', errorMessage);
      setError(errorMessage);
      throw err;
    }
  };

  const joinGame = async (player: Player) => {
    if (!gameId) {
      const error = 'No game ID provided';
      setError(error);
      throw new Error(error);
    }
    
    setError(null);
    try {
      console.log('useGame: Joining game:', gameId, 'as player:', player);
      await gameService.joinGame(gameId, player);
      console.log('useGame: Successfully joined game');
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Unknown error occurred';
      console.error('useGame: Failed to join game:', errorMessage);
      setError(errorMessage);
      throw err;
    }
  };

  const submitDefinition = async (definition: Definition) => {
    if (!gameId) {
      const error = 'No game ID provided';
      setError(error);
      throw new Error(error);
    }

    setError(null);
    try {
      await gameService.submitDefinition(gameId, definition);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to submit definition';
      setError(errorMessage);
      throw err;
    }
  };

  const submitVote = async (definitionId: string, voterId: string) => {
    if (!gameId) {
      const error = 'No game ID provided';
      setError(error);
      throw new Error(error);
    }

    setError(null);
    try {
      await gameService.submitVote(gameId, definitionId, voterId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to submit vote';
      setError(errorMessage);
      throw err;
    }
  };

  const startNewRound = async () => {
    if (!gameId) {
      const error = 'No game ID provided';
      setError(error);
      throw new Error(error);
    }

    setError(null);
    try {
      await gameService.startNewRound(gameId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to start new round';
      setError(errorMessage);
      throw err;
    }
  };

  const endGame = async () => {
    if (!gameId) {
      const error = 'No game ID provided';
      setError(error);
      throw new Error(error);
    }

    setError(null);
    try {
      await gameService.endGame(gameId);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Failed to end game';
      setError(errorMessage);
      throw err;
    }
  };

  return {
    game,
    error,
    loading,
    createGame,
    joinGame,
    submitDefinition,
    submitVote,
    startNewRound,
    endGame,
  };
} 